import { Controller } from "stimulus";
import { FetchRequest } from "@rails/request.js";

/**
 * Manages searching and selecting recipients (ContactMethods or Credentials)
 * for UserNotification::Rule forms. Adds hidden fields for nested attributes.
 *
 * Data attributes:
 *   data-recipient-picker-search-url: URL for the search endpoint
 */
export default class extends Controller {
  static targets = ["input", "results", "list", "template"];

  connect() {
    this.searchUrl = this.data.get("searchUrl");
    this.nextIndex = this.existingRecipientCount();
    this.debounceTimer = null;
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
  }

  search() {
    clearTimeout(this.debounceTimer);
    const query = this.inputTarget.value.trim();

    if (query.length < 2) {
      this.resultsTarget.innerHTML = "";
      this.resultsTarget.classList.add("hidden");
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 300);
  }

  async fetchResults(query) {
    const url = `${this.searchUrl}?q=${encodeURIComponent(query)}`;

    try {
      const request = new FetchRequest("get", url, { responseKind: "json" });
      const response = await request.perform();

      if (response.ok) {
        const results = await response.json;
        this.renderResults(results);
      }
    } catch (error) {
      console.error("Recipient search error:", error);
    }
  }

  renderResults(results) {
    this.resultsTarget.innerHTML = "";

    if (results.length === 0) {
      const empty = document.createElement("div");
      empty.className = "p-2 text-sm text-gray-500";
      empty.textContent = "No results found";
      this.resultsTarget.appendChild(empty);
      this.resultsTarget.classList.remove("hidden");
      return;
    }

    results.forEach((r) => {
      const item = document.createElement("div");
      item.className = "p-2 cursor-pointer hover:bg-gray-100 text-sm";
      item.dataset.action = "click->recipient-picker#select";
      item.dataset.recipientId = String(r.id);
      item.dataset.recipientType = String(r.type);
      item.dataset.recipientLabel = r.label != null ? String(r.label) : "";
      item.dataset.recipientDetail = r.detail != null ? String(r.detail) : "";

      const labelSpan = document.createElement("span");
      labelSpan.className = "font-medium";
      labelSpan.textContent = r.label != null ? String(r.label) : "";

      const detailSpan = document.createElement("span");
      detailSpan.className = "text-gray-500 ml-1";
      detailSpan.textContent = `(${r.detail != null ? String(r.detail) : ""})`;

      item.appendChild(labelSpan);
      item.appendChild(detailSpan);
      this.resultsTarget.appendChild(item);
    });

    this.resultsTarget.classList.remove("hidden");
  }

  select(event) {
    const el = event.currentTarget;
    const id = el.dataset.recipientId;
    const type = el.dataset.recipientType;
    const label = el.dataset.recipientLabel;
    const detail = el.dataset.recipientDetail;

    if (this.alreadyAdded(type, id)) {
      this.resultsTarget.innerHTML = "";
      this.resultsTarget.classList.add("hidden");
      this.inputTarget.value = "";
      return;
    }

    this.addRecipient({ id, type, label, detail });
    this.resultsTarget.innerHTML = "";
    this.resultsTarget.classList.add("hidden");
    this.inputTarget.value = "";
  }

  addRecipient({ id, type, label, detail }) {
    const idx = this.nextIndex++;
    const prefix = `user_notification_rule[rule_recipients_attributes][${idx}]`;

    const row = document.createElement("div");
    row.classList.add("flex", "items-center", "gap-2", "p-2", "bg-white", "rounded-md", "border", "border-gray-200");
    row.dataset.recipientPickerRecipientType = type;
    row.dataset.recipientPickerRecipientId = id;
    const typeInput = document.createElement("input");
    typeInput.type = "hidden";
    typeInput.name = `${prefix}[recipientable_type]`;
    typeInput.value = type;

    const idInput = document.createElement("input");
    idInput.type = "hidden";
    idInput.name = `${prefix}[recipientable_id]`;
    idInput.value = id;

    const textContainer = document.createElement("span");
    textContainer.classList.add("text-sm", "flex-1");

    const labelSpan = document.createElement("span");
    labelSpan.classList.add("font-medium");
    labelSpan.textContent = label;

    const detailSpan = document.createElement("span");
    detailSpan.classList.add("text-gray-500");
    detailSpan.textContent = ` (${detail})`;

    textContainer.appendChild(labelSpan);
    textContainer.appendChild(detailSpan);

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.dataset.action = "click->recipient-picker#remove";
    removeButton.classList.add("text-gray-400", "hover:text-rose-600", "text-sm");
    removeButton.textContent = "\u00d7";

    row.appendChild(typeInput);
    row.appendChild(idInput);
    row.appendChild(textContainer);
    row.appendChild(removeButton);
    this.listTarget.appendChild(row);
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-recipient-picker-recipient-type]");
    const destroyInput = row.querySelector("input[name*='_destroy']");
    if (destroyInput) {
      destroyInput.value = "1";
      row.classList.add("hidden");
    } else {
      row.remove();
    }
  }

  alreadyAdded(type, id) {
    return this.listTarget.querySelector(
      `[data-recipient-picker-recipient-type="${type}"][data-recipient-picker-recipient-id="${id}"]:not(.hidden)`
    ) !== null;
  }

  existingRecipientCount() {
    return this.hasListTarget ? this.listTarget.querySelectorAll("[data-recipient-picker-recipient-type]").length : 0;
  }

  hideResults(event) {
    if (!this.element.contains(event.relatedTarget)) {
      setTimeout(() => {
        this.resultsTarget.classList.add("hidden");
      }, 200);
    }
  }
}
